import { Helmet } from "react-helmet";
import { useState } from "react";
import { Camera, Store, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { usePortalLanguage, tLang } from "@/contexts/PortalLanguageContext";
import { useLocation } from "react-router-dom";
import { supabase } from "@/lib/supabase/client";

// Brand tokens
const NAVY  = "#1A1A2E";
const EMBER = "#FF6B35";
const TEAL  = "#00BFA5";

// Inline email regex — just enough to catch obvious typos before the round-trip
const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

// ─── Shared email-capture form ────────────────────────────────────────────────

type FormProps = {
  email:     string;
  setEmail:  (v: string) => void;
  busy:      boolean;
  done:      boolean;
  formError: string | null;
  onSubmit:  (e: React.FormEvent) => void;
  ctaLabel:  string;
  note?:     string;
  successMsg: string;
  showBadges?: boolean;
  appStoreUrl: string;
  playStoreUrl: string;
};

function EarlyAccessForm({
  email, setEmail, busy, done, formError, onSubmit,
  ctaLabel, note, successMsg, showBadges, appStoreUrl, playStoreUrl,
}: FormProps) {
  if (done) {
    return (
      <div
        className="rounded-xl px-4 sm:px-6 lg:px-8 py-5 text-center text-sm font-medium"
        style={{ backgroundColor: `${TEAL}22`, color: TEAL, border: `1px solid ${TEAL}44` }}
      >
        {successMsg}
      </div>
    );
  }

  return (
    <div className="w-full">
      <form onSubmit={onSubmit} noValidate className="flex flex-col sm:flex-row gap-3">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          required
          className="
            flex-1 bg-white/10 border-white/20 text-white
            placeholder:text-gray-500
            focus-visible:ring-1 focus-visible:ring-white/30
            focus-visible:border-white/40
          "
          aria-label="Email address"
        />
        <button
          type="submit"
          disabled={busy}
          className="shrink-0 rounded-full px-8 py-2.5 text-sm font-medium text-white transition disabled:opacity-60 hover:opacity-90"
          style={{ backgroundColor: EMBER, boxShadow: "0 4px 14px rgba(255,107,53,0.3)" }}
        >
          {busy ? "…" : ctaLabel}
        </button>
      </form>

      {formError && (
        <p className="mt-2 text-xs text-red-400">{formError}</p>
      )}

      {note && !formError && (
        <p className="mt-3 text-xs text-gray-500">{note}</p>
      )}

      {showBadges && (
        <div className="flex flex-wrap gap-4 mt-6 items-center">
          <a
            href={appStoreUrl}
            target="_blank"
            rel="noreferrer"
            className="transition-transform hover:scale-105"
          >
            <img
              src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
              alt="Download on the App Store"
              className="h-10"
              loading="lazy"
            />
          </a>
          <a
            href={playStoreUrl}
            target="_blank"
            rel="noreferrer"
            className="transition-transform hover:scale-105"
          >
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
              alt="Get it on Google Play"
              className="h-10"
              loading="lazy"
            />
          </a>
        </div>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AppPage() {
  const { t } = usePortalLanguage();
  const { pathname } = useLocation();
  const locale = pathname.startsWith("/fr") ? "fr" : pathname.startsWith("/es") ? "es" : "en";

  const [email,     setEmail]     = useState("");
  const [busy,      setBusy]      = useState(false);
  const [done,      setDone]      = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const appStoreUrl  = import.meta.env.VITE_APPSTORE_URL  ?? "#";
  const playStoreUrl = import.meta.env.VITE_PLAYSTORE_URL ?? "#";

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!isValidEmail(email)) {
      setFormError(t("app.form.invalidEmail", "Please enter a valid email address."));
      return;
    }

    setBusy(true);
    try {
      const { error } = await supabase
        .from("early_access_signups")
        .insert([{ email: email.trim(), locale, created_at: new Date().toISOString() }]);

      // 23505 = unique_violation — email already registered, still show success
      if (error && error.code !== "23505") throw error;

      setDone(true);
    } catch (err: any) {
      setFormError(
        err?.message ?? t("app.form.error", "Something went wrong. Please try again.")
      );
    } finally {
      setBusy(false);
    }
  };

  // ─── Section data ──────────────────────────────────────────────────────────

  const steps = [
    {
      icon:  Camera,
      title: t("app.steps.scan.title", "Point your phone at the space"),
      desc:  t("app.steps.scan.desc",  "Open Fyrris and aim your camera at any room. The app reads the space in seconds — no tape measure, no guesswork."),
    },
    {
      icon:  Store,
      title: t("app.steps.browse.title", "Browse products from local HPB retailers"),
      desc:  t("app.steps.browse.desc", "Explore fireplaces, grills, and patio furniture from showrooms near you. Everything in the catalog is available to buy locally."),
    },
    {
      icon:  MapPin,
      title: t("app.steps.visit.title", "See it in AR — then visit the showroom"),
      desc:  t("app.steps.visit.desc",  "Place any product in your room at true scale. When you're confident, the app shows you exactly which store carries it."),
    },
  ];

  const sharedFormProps: FormProps = {
    email,
    setEmail,
    busy,
    done,
    formError,
    onSubmit,
    ctaLabel:   t("app.form.cta",     "Get Early Access"),
    successMsg: t("app.form.success", "You're on the list. We'll notify you when Fyrris launches in your area."),
    appStoreUrl,
    playStoreUrl,
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Helmet>
        <title>{tLang(locale, "app.meta.title", "Fyrris App — Visualize Fireplaces, BBQ & Patio in Your Home")}</title>
        <meta name="description" content={tLang(locale, "app.meta.description", "Free AR app for homeowners. See exactly how any fireplace, BBQ grill, or patio product looks in your actual space — then find the nearest HPB showroom that carries it. iOS & Android.")} />
        <meta name="keywords" content="fireplace AR app, BBQ visualization app, patio design augmented reality, homeowner AR tool, HPB products app, iOS Android fireplace app" />
        <meta property="og:title" content={tLang(locale, "app.meta.ogTitle", "Fyrris App — Visualize Fireplaces, BBQ & Patio in Your Home")} />
        <meta property="og:description" content={tLang(locale, "app.meta.ogDescription", "Free AR app for homeowners. See exactly how any fireplace, BBQ grill, or patio product looks in your space before visiting a local HPB showroom.")} />
        <meta property="og:image" content="https://www.fyrris.com/fyrris-social-banner.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={tLang(locale, "app.meta.ogTitle", "Fyrris App — Visualize Fireplaces, BBQ & Patio in Your Home")} />
        <meta name="twitter:description" content={tLang(locale, "app.meta.ogDescription", "Free AR app for homeowners. See how any fireplace, BBQ grill, or patio product looks in your space before visiting a local HPB showroom.")} />
      </Helmet>

      {/* ── SECTION 1 · HERO ──────────────────────────────────────────────── */}
      <section
        className="min-h-screen flex flex-col justify-center pt-12 pb-20 px-4 sm:px-6 lg:px-8"
        style={{
          backgroundImage: `linear-gradient(rgba(26,26,46,0.55), rgba(26,26,46,0.55)), url('/hero-ar-demo.png')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="max-w-2xl mx-auto w-full">
          {/* Eyebrow */}
          <p
            className="inline-block text-xs font-semibold tracking-widest uppercase mb-6 px-3 py-1 rounded-full"
            style={{ backgroundColor: `${TEAL}22`, color: TEAL }}
          >
            {t("app.hero.eyebrow", "Free for homeowners")}
          </p>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
            {t("app.hero.headline", "See it before you buy it.")}
          </h1>

          {/* Subhead */}
          <p className="text-lg md:text-xl text-gray-300 mb-10 leading-relaxed">
            {t(
              "app.hero.subhead",
              "Visualize any fireplace, grill, or patio product in your actual space — free on iOS and Android."
            )}
          </p>

          {/* Email capture */}
          <EarlyAccessForm
            {...sharedFormProps}
            showBadges
          />

          {/* iOS launch badge */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            backgroundColor: 'rgba(0,191,165,0.15)',
            border: '1px solid rgba(0,191,165,0.4)',
            borderRadius: '9999px',
            padding: '0.5rem 1rem',
            marginTop: '1.5rem',
            marginBottom: '1rem',
          }}>
            <span style={{
              width: '8px', height: '8px',
              borderRadius: '50%',
              backgroundColor: '#00BFA5',
              display: 'inline-block',
            }} />
            <span style={{
              color: '#00BFA5',
              fontSize: '0.875rem',
              fontWeight: '600',
            }}>
              {t("app.hero.note", "Coming soon to the App Store. Free for homeowners.")}
            </span>
          </div>
        </div>
      </section>

      {/* ── SECTION 2 · HOW IT WORKS ──────────────────────────────────────── */}
      <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 bg-gray-950">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            {t("app.howItWorks.title", "How it works")}
          </h2>
          <p className="text-center text-gray-300 mb-14 max-w-xl mx-auto">
            {t("app.howItWorks.subtitle", "Three steps from curious to confident.")}
          </p>

          <div className="grid md:grid-cols-3 gap-10">
            {steps.map(({ icon: Icon, title, desc }, idx) => (
              <div key={title} className="relative flex flex-col gap-4">
                {/* Step number */}
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                  style={{ backgroundColor: EMBER }}
                >
                  {idx + 1}
                </div>

                <Icon className="w-7 h-7" style={{ color: TEAL }} />

                <h3 className="text-xl font-semibold text-white">{title}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{desc}</p>

                {/* Connector line (not on last step) */}
                {idx < steps.length - 1 && (
                  <div
                    className="hidden md:block absolute top-5 left-full w-full h-px -translate-x-4"
                    style={{ backgroundColor: `${EMBER}33` }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 3 · REPEAT CTA ────────────────────────────────────────── */}
      <section
        className="py-20 md:py-28 px-4 sm:px-6 lg:px-8"
        style={{ backgroundColor: NAVY }}
      >
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t("app.cta.headline", "Be first to know.")}
          </h2>
          <p className="text-gray-300 mb-8 leading-relaxed">
            {t(
              "app.cta.body",
              "Already excited? Tell a friend. The more homeowners who join early, the faster Fyrris reaches their area — and their local retailers."
            )}
          </p>

          <div className="text-left">
            <EarlyAccessForm {...sharedFormProps} />
          </div>
        </div>
      </section>
    </>
  );
}
